import { api } from './api.js';
import { isStandalone } from './install.js';

/**
 * Push notifications on this device.
 * - 'unsupported'    browser has no Web Push
 * - 'ios-install'    iPhone/iPad: only works once added to the Home Screen
 * - 'not-configured' server has no VAPID keys yet
 * - 'denied'         user blocked notifications for this site
 * - 'off' / 'on'
 */
export type PushState = 'unsupported' | 'ios-install' | 'not-configured' | 'denied' | 'off' | 'on';

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

// The service worker only exists in production builds; don't hang in dev.
async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
  ]);
}

let serverConfig: Promise<{ configured: boolean; publicKey: string | null }> | undefined;
function getServerConfig() {
  serverConfig ??= api.get<{ configured: boolean; publicKey: string | null }>('/me/push')
    .catch((e) => { serverConfig = undefined; throw e; });
  return serverConfig;
}

export async function getPushState(): Promise<PushState> {
  if (!supported()) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  const { configured } = await getServerConfig();
  if (!configured) return 'not-configured';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  if (sub && Notification.permission === 'granted') {
    // Keep the server copy fresh (also re-links the device if another user
    // had it, or if the server dropped it as expired).
    void api.put('/me/push', sub.toJSON()).catch(() => {});
    return 'on';
  }
  return 'off';
}

function base64UrlToBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(b64url.length / 4) * 4, '=');
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Ask permission (must run from a tap) and register this device. */
export async function enablePush(): Promise<PushState> {
  const { configured, publicKey } = await getServerConfig();
  if (!configured || !publicKey) return 'not-configured';

  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'off';

  const reg = await registration();
  if (!reg) return 'unsupported';
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with a different key can't be reused.
  const current = sub?.options.applicationServerKey;
  if (sub && current && !sameKey(new Uint8Array(current), base64UrlToBytes(publicKey))) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(publicKey),
  });
  await api.put('/me/push', sub.toJSON());
  return 'on';
}

function sameKey(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Turn notifications off on this device. */
export async function disablePush(): Promise<PushState> {
  await forgetThisDevice();
  return 'off';
}

/**
 * Unlink this device from the signed-in user — call before signing out so
 * the next person on this phone doesn't get their notifications.
 * Best effort: never blocks sign-out.
 */
export async function forgetThisDevice(): Promise<void> {
  try {
    if (!supported()) return;
    const reg = await registration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api.post('/me/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
  } catch {
    // ignore
  }
}

export function sendTestPush() {
  return api.post<{ sent: number }>('/me/push/test', {});
}
